import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_URLS } from '../../config/Constants';
import { SharedContent, shareService } from '../../services/shareService';
import PlayersReportTable, { PlayerBase } from '../../components/shared/PlayersReportTable';

interface SharedTournamentPlayersLeaderboardProps {
  shareData: SharedContent;
  fetchPath?: string;
  loadingLabel?: string;
  defaultTitle?: string;
  loadErrorMessage?: string;
}

interface TournamentPlayer extends PlayerBase {
  gradYear: string;
  teamName: string;
  groundBalls: number;
  turnovers: number;
  causedTurnovers: number;
  shotsSaved: number;
  npc: number;
  goalsPlusMinus: number;
  shots: number;
  saved: number;
  goals: number;
  assists: number;
  points: number;
  shotPercentage: number;
  sogPercentage: number;
  missedShots: number;
  shotsOnGoal: number;
  goalsAllowed: number;
  faceoffs: number;
  won: number;
  lost: number;
  winPercentage: number;
  penalties: number;
  blockedShots: number;
}

const formatPosition = (position?: string | string[]) => {
  const formatOne = (pos?: string) =>
    pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos || '-';

  return Array.isArray(position) ? position.map(formatOne).join(' | ') : formatOne(position);
};

const SharedTournamentPlayersLeaderboard: React.FC<SharedTournamentPlayersLeaderboardProps> = ({
  shareData,
  fetchPath,
  loadingLabel = 'Loading tournament leaderboard...',
  defaultTitle = 'Tournament Players Leaderboard',
  loadErrorMessage = 'Failed to load tournament leaderboard',
}) => {
  const [players, setPlayers] = useState<TournamentPlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offenseSortConfig, setOffenseSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({
    key: 'points',
    direction: 'desc',
  });
  const [possessionSortConfig, setPossessionSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({
    key: 'groundBalls',
    direction: 'desc',
  });
  const [goalieSortConfig, setGoalieSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({
    key: 'SV',
    direction: 'desc',
  });
  const [faceoffSortConfig, setFaceoffSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({
    key: 'winPercentage',
    direction: 'desc',
  });
  const [penaltySortConfig, setPenaltySortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>({
    key: 'penalties',
    direction: 'desc',
  });
  const [blockedShotsSortConfig, setBlockedShotsSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'blockedShots',
    direction: 'desc',
  });

  const loadPlayers = useCallback(
    async (showSpinner = false) => {
      try {
        if (showSpinner) setLoading(true);
        setError(null);
        const response = await shareService.publicFetch(
          fetchPath || `${API_URLS.BASE_URL}/share/tournament/players/${shareData.shareId}`,
        );

        if (!response.data.success) {
          throw new Error(response.data.message || loadErrorMessage);
        }

        const rawPlayers = response.data.data.players || [];
        const processed = rawPlayers.map((player: any) => {
          const groundBalls = player.groundballs || 0;
          const turnovers = player.turnovers || 0;
          const causedTurnovers = player.causedturnover || 0;
          const shotsSaved = player.shotsaved || 0;
          const shots = player.shots || 0;
          const saved = player.saved || 0;
          const goals = player.goals || 0;
          const assists = player.assist || 0;
          const missedShots = player.shotmissed || 0;
          const points = goals + assists;
          const shotsOnGoal = player.shotsongoal || 0;
          const goalsAllowed = player.goalsallowed || Math.max(shotsOnGoal - saved, 0);
          const won = player.faceoffwins || 0;
          const lost = player.faceofflosses || 0;
          const faceoffs = won + lost;
          const npc = groundBalls - (turnovers - causedTurnovers) - shotsSaved;

          return {
            playerId: player._id,
            playerNumber: player.no,
            playerName: `${player.firstName || ''} ${player.lastName || ''}`.trim(),
            gradYear: player.graduation ? String(player.graduation) : '-',
            position: player.position || player.positions || [],
            gamesPlayed: player.gamesPlayed || player.gameCount || 0,
            teamName: player.teamName || player.clientName || 'Unknown Team',
            groundBalls,
            turnovers,
            causedTurnovers,
            shotsSaved,
            npc,
            goalsPlusMinus: npc * 0.25,
            shots,
            saved,
            goals,
            assists,
            points,
            shotPercentage: shots > 0 ? Number(((goals / shots) * 100).toFixed(0)) : 0,
            sogPercentage: shots > 0 ? Math.ceil(((goals + shotsSaved) / shots) * 100) : 0,
            missedShots,
            shotsOnGoal,
            goalsAllowed,
            faceoffs,
            won,
            lost,
            winPercentage: faceoffs > 0 ? Number(((won / faceoffs) * 100).toFixed(0)) : 0,
            penalties: player.penalties || 0,
            blockedShots: player.blocks || 0,
          };
        });

        setPlayers(processed);
      } catch (err: any) {
        setError(err.message || loadErrorMessage);
      } finally {
        setLoading(false);
      }
    },
    [fetchPath, loadErrorMessage, shareData.shareId],
  );

  useEffect(() => {
    loadPlayers(true);
    const interval = window.setInterval(() => loadPlayers(false), 30000);
    return () => window.clearInterval(interval);
  }, [loadPlayers]);

  const teamColumn = {
    header: 'Team',
    key: 'teamName' as keyof TournamentPlayer,
    sortable: true,
  };

  const gradYearColumn = {
    header: 'Grad Year',
    key: 'gradYear' as keyof TournamentPlayer,
    sortable: true,
  };

  const playerColumn = {
    header: 'Player',
    key: 'special' as keyof TournamentPlayer,
    sortable: true,
    render: (player: TournamentPlayer) => (
      <div className="text-left">
        <div className="font-semibold">
          {player.playerNumber} {player.playerName}
        </div>
      </div>
    ),
  };

  const positionColumn = {
    header: 'Position',
    key: 'special' as keyof TournamentPlayer,
    sortable: true,
    render: (player: TournamentPlayer) => <div className="whitespace-nowrap">{formatPosition(player.position)}</div>,
  };

  const baseColumns = [playerColumn, gradYearColumn, teamColumn, positionColumn];

  const possessionColumns = [
    ...baseColumns,
    {
      header: 'GP',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'GB', key: 'groundBalls' as keyof TournamentPlayer, sortable: true },
    { header: 'TO', key: 'turnovers' as keyof TournamentPlayer, sortable: true },
    {
      header: 'TO/PG',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => (player.turnovers / (player.gamesPlayed || 1)).toFixed(1),
    },
    { header: 'CTO', key: 'causedTurnovers' as keyof TournamentPlayer, sortable: true },
    {
      header: 'CTO/PG',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => (player.causedTurnovers / (player.gamesPlayed || 1)).toFixed(1),
    },
    { header: 'SS', key: 'shotsSaved' as keyof TournamentPlayer, sortable: true },
    { header: 'NPC', key: 'npc' as keyof TournamentPlayer, sortable: true },
    { header: 'G+/-', key: 'goalsPlusMinus' as keyof TournamentPlayer, sortable: true },
  ];

  const offenseColumns = [
    ...baseColumns,
    {
      header: 'GP',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'S', key: 'shots' as keyof TournamentPlayer, sortable: true },
    { header: 'Missed', key: 'missedShots' as keyof TournamentPlayer, sortable: true },
    { header: 'SS', key: 'shotsSaved' as keyof TournamentPlayer, sortable: true },
    { header: 'G', key: 'goals' as keyof TournamentPlayer, sortable: true },
    {
      header: 'S%',
      key: 'shotPercentage' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => `${player.shotPercentage}%`,
    },
    {
      header: 'SOG%',
      key: 'sogPercentage' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => `${player.sogPercentage}%`,
    },
    { header: 'A', key: 'assists' as keyof TournamentPlayer, sortable: true },
    { header: 'P', key: 'points' as keyof TournamentPlayer, sortable: true },
    {
      header: 'PPG',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => (player.points / (player.gamesPlayed || 1)).toFixed(1),
    },
    {
      header: 'GPG',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => (player.goals / (player.gamesPlayed || 1)).toFixed(1),
    },
  ];

  const goalieColumns = [
    ...baseColumns,
    {
      header: 'GP',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'SOG', key: 'shotsOnGoal' as keyof TournamentPlayer, sortable: true },
    {
      header: 'SV',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => {
        const savePercentage =
          player.shotsOnGoal > 0 ? Number(((player.saved / player.shotsOnGoal) * 100).toFixed(0)) : 0;
        return (
          <div>
            <div>{player.saved}</div>
            <div className="text-xs text-gray-600">({savePercentage.toFixed(1)}%)</div>
          </div>
        );
      },
    },
    {
      header: 'SPG',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => (player.saved / (player.gamesPlayed || 1)).toFixed(1),
    },
    { header: 'GA', key: 'goalsAllowed' as keyof TournamentPlayer, sortable: true },
    {
      header: 'GAPG',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (player: TournamentPlayer) => (player.goalsAllowed / (player.gamesPlayed || 1)).toFixed(1),
    },
  ];

  const faceoffColumns = [
    ...baseColumns,
    {
      header: 'GP',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'FO', key: 'faceoffs' as keyof TournamentPlayer, sortable: true },
    { header: 'W', key: 'won' as keyof TournamentPlayer, sortable: true },
    {
      header: 'W%',
      key: 'winPercentage' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => `${p.winPercentage}%`,
    },
    { header: 'L', key: 'lost' as keyof TournamentPlayer, sortable: true },
  ];

  const penaltyColumns = [
    ...baseColumns,
    {
      header: 'GP',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'Penalties', key: 'penalties' as keyof TournamentPlayer, sortable: true },
  ];

  const blockedShotsColumns = [
    ...baseColumns,
    {
      header: 'GP',
      key: 'special' as keyof TournamentPlayer,
      sortable: true,
      render: (p: TournamentPlayer) => p.gamesPlayed ?? 0,
    },
    { header: 'Blocked Shots', key: 'blockedShots' as keyof TournamentPlayer, sortable: true },
  ];

  const sections = useMemo(
    () => ({
      possession: players.filter(
        (player) => player.groundBalls + player.turnovers + player.causedTurnovers + player.shotsSaved > 0,
      ),
      offense: players.filter(
        (player) => player.shots + player.missedShots + player.shotsSaved + player.goals + player.assists > 0,
      ),
      goalie: players.filter((player) => player.shotsOnGoal > 0),
      faceoff: players.filter((player) => player.faceoffs > 0),
      penalties: players.filter((player) => player.penalties > 0),
      blockedShots: players.filter((player) => player.blockedShots > 0),
    }),
    [players],
  );

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#05396b] mx-auto mb-4"></div>
        <p className="text-gray-600">{loadingLabel}</p>
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-12 text-red-600">{error}</div>;
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="border border-[#bccedf] rounded-lg shadow-sm overflow-hidden">
          <div className="p-2 sm:p-6">
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#05396b]">{shareData.name || defaultTitle}</h1>
            </div>

            <PlayersReportTable
              title="Offense"
              columns={offenseColumns}
              players={sections.offense}
              sortConfig={offenseSortConfig}
              onSortChange={setOffenseSortConfig}
            />
            <PlayersReportTable
              title="Possession"
              columns={possessionColumns}
              players={sections.possession}
              sortConfig={possessionSortConfig}
              onSortChange={setPossessionSortConfig}
            />
            <PlayersReportTable
              title="Goalie"
              columns={goalieColumns}
              players={sections.goalie}
              sortConfig={goalieSortConfig}
              onSortChange={setGoalieSortConfig}
            />
            <PlayersReportTable
              title="Face-offs"
              columns={faceoffColumns}
              players={sections.faceoff}
              sortConfig={faceoffSortConfig}
              onSortChange={setFaceoffSortConfig}
            />
            <PlayersReportTable
              title="Penalties"
              columns={penaltyColumns}
              players={sections.penalties}
              sortConfig={penaltySortConfig}
              onSortChange={setPenaltySortConfig}
            />
            <PlayersReportTable
              title="Blocked Shots"
              columns={blockedShotsColumns}
              players={sections.blockedShots}
              sortConfig={blockedShotsSortConfig}
              onSortChange={setBlockedShotsSortConfig}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedTournamentPlayersLeaderboard;
